import { UntitledIcon } from '@faceup/icons'
import { ulTranslate02 } from '@faceup/icons/ulTranslate02'
import { ulX } from '@faceup/icons/ulX'
import { useLanguageName } from '@faceup/localization'
import { defineStorageItem, twMerge, useStorageItem } from '@faceup/ui'
import { Button, Checkbox, ModalConfirm, Select, Space, Tooltip, useMessage } from '@faceup/ui-base'
import { DEFAULT_LANGUAGE, type Language } from '@faceup/utils'
import { yup } from '@faceup/validation'
import { useEffect, useState } from 'react'

type TranslateMenuProps = {
  defaultLanguages: [source: Language | 'auto', target: Language | null]
  onTranslate: (source: Language | 'auto', target: Language) => void
  onShowOriginal: () => void
  loading?: boolean
  disabledReason?: 'e2ee'
  translations: {
    detectLanguage: string
    translate: string
    showOriginal: string
    to: string
    sameLanguageError: string
    disabledReasonE2EE: string
    onUseWarningTitle: string
    onUseWarningContent: string
    onUseWarningDoNotShowAgain: string
  }
  onClose?: () => void
}

const translationWarningOptOut = defineStorageItem({
  key: 'translationWarningModalDisable',
  schema: yup.boolean().required(),
  defaultValue: false,
})

export const TranslateMenu = ({
  defaultLanguages: [defaultSource, defaultTarget],
  onTranslate,
  onShowOriginal,
  translations,
  loading = false,
  disabledReason,
  onClose,
}: TranslateMenuProps) => {
  const [source, setSource] = useState<Language | 'auto'>('auto')
  const [target, setTarget] = useState<Language>(DEFAULT_LANGUAGE)
  const [isConfirmModalDisplayed, setIsConfirmModalDisplayed] = useState(false)
  const [isWarningModalDisabled, setIsWarningModalDisabled] =
    useStorageItem(translationWarningOptOut)
  const message = useMessage()
  const { getLanguageList } = useLanguageName()
  useEffect(() => {
    setSource(defaultSource ?? 'auto')
    setTarget(defaultTarget ?? DEFAULT_LANGUAGE)
  }, [defaultSource, defaultTarget])

  const triggerOnShowOriginal = onShowOriginal
  const triggerOnTranslate = () => {
    if (source === target) {
      message.error(translations.sameLanguageError)
      setIsConfirmModalDisplayed(false)
      return
    }

    onTranslate(source, target)
    setIsConfirmModalDisplayed(false)
  }

  const isTranslated = defaultSource === source && defaultTarget === target
  const translateButton = (
    <Button
      type='link'
      size='small'
      loading={loading}
      disabled={Boolean(disabledReason)}
      onClick={() => {
        if (isTranslated) {
          triggerOnShowOriginal()
        } else if (isWarningModalDisabled) {
          triggerOnTranslate()
        } else {
          setIsConfirmModalDisplayed(true)
        }
      }}
      data-test='translate'
    >
      {isTranslated ? translations.showOriginal : translations.translate}
    </Button>
  )

  return (
    <div
      style={{
        padding: `3px ${onClose === undefined ? '12px' : '4px'} 3px 9px`,
        border: '1px solid #e1e5e8',
        borderRadius: 16,
      }}
    >
      <Space wrap style={{ justifyContent: 'center' }}>
        <UntitledIcon icon={ulTranslate02} />
        <Select
          className={twMerge(
            'text-[12px]',
            isTranslated ? 'color-[#688699]' : 'color-[#062d46] font-bold'
          )}
          size='small'
          variant='borderless'
          value={source}
          disabled={Boolean(disabledReason)}
          onChange={language => setSource(language as Language | 'auto')}
          data-test='source-language-select'
          options={[
            { label: translations.detectLanguage, value: 'auto' },
            ...getLanguageList().map(({ language, translatedName }) => ({
              label: translatedName,
              value: language,
            })),
          ]}
        />
        <span style={{ color: '#688699' }}>{translations.to}</span>
        <Select
          className={twMerge(
            'text-[12px]',
            isTranslated ? 'color-[#062d46] font-bold' : 'color-[#688699]'
          )}
          size='small'
          variant='borderless'
          disabled={Boolean(disabledReason)}
          value={target ?? DEFAULT_LANGUAGE}
          onChange={language => setTarget(language)}
          data-test='target-language-select'
          options={getLanguageList().map(({ language, translatedName }) => ({
            label: translatedName,
            value: language,
          }))}
        />
        {disabledReason ? (
          <Tooltip title={disabledReason === 'e2ee' ? translations.disabledReasonE2EE : ''}>
            {translateButton}
          </Tooltip>
        ) : (
          translateButton
        )}
        {onClose !== undefined && (
          <Button
            shape='circle'
            size='small'
            onClick={() => {
              triggerOnShowOriginal()
              onClose()
            }}
            data-test='close-translation'
          >
            <UntitledIcon icon={ulX} />
          </Button>
        )}
      </Space>
      <ModalConfirm
        open={isConfirmModalDisplayed}
        title={translations.onUseWarningTitle}
        onConfirm={triggerOnTranslate}
        onCancel={() => {
          setIsConfirmModalDisplayed(false)
          setIsWarningModalDisabled(false)
        }}
        data-test='translation-warning-modal'
      >
        <Space direction='vertical'>
          <p>{translations.onUseWarningContent}</p>
          <Checkbox
            checked={isWarningModalDisabled}
            onChange={() => setIsWarningModalDisabled(!isWarningModalDisabled)}
          >
            {translations.onUseWarningDoNotShowAgain}
          </Checkbox>
        </Space>
      </ModalConfirm>
    </div>
  )
}
